import { Injectable } from '@angular/core';
import { Insomnia } from '@awesome-cordova-plugins/insomnia/ngx';
import { combineLatest, from, Observable, of } from 'rxjs';
import { equals, isEmpty, isNil } from '@qld-recreational/ramda';
import { map, mapTo, switchMap, tap } from 'rxjs/operators';
import {
  ContentfulContext,
  contentfulNewContentMessage,
  ContentfulSpaceTagId,
  getAssetFile,
  getId,
  ICacheAsset,
  IContentfulEntry,
  IContentfulState,
  ISyncCollection,
} from './contentful-library.model';
import { Store } from '@ngrx/store';
import { Asset, createClient, Entry, FieldsType } from 'contentful';
import { StorageService } from '@qld-recreational/storage';
import { HttpClient } from '@angular/common/http';
import { BLOCKS, Document } from '@contentful/rich-text-types';
import {
  documentToHtmlString,
  Options,
} from '@contentful/rich-text-html-renderer';
import { AlertService } from '@qld-recreational/alert';
import { contentfulConfig } from './contentful-library.config';
import {
  assetsDownloaded,
  clearNextSyncToken,
  nextSyncTokenUpdate,
  updateAssetsCached,
  updateAssetsDownloaded,
  updateAssetsToCache,
  updateEntriesCached,
  updateEntriesToCache,
} from './contentful-library.actions';

@Injectable({
  providedIn: 'root',
})
export class ContentfulLibraryService {
  private readonly ASSET_READ_TIMEOUT_MS = 10000;
  private readonly ASSET_STORE_TIMEOUT_MS = 10000;

  constructor(
    private http: HttpClient,
    private insomnia: Insomnia,
    private contentfulStore: Store<IContentfulState>,
    private storage: StorageService,
    private alertService: AlertService,
  ) {}

  public preventScreenSleep() {
    this.insomnia.keepAwake();
  }

  public allowScreenSleep() {
    this.insomnia.allowSleepAgain();
  }

  private getContentfulClient(context: ContentfulContext) {
    return createClient({
      host: equals(context, ContentfulContext.STAGING)
        ? contentfulConfig.staging.host
        : contentfulConfig.production.host,
      accessToken: equals(context, ContentfulContext.STAGING)
        ? contentfulConfig.staging.accessToken
        : contentfulConfig.production.accessToken,
      space: equals(context, ContentfulContext.STAGING)
        ? contentfulConfig.staging.space
        : contentfulConfig.production.space,
    });
  }

  public getEntryById<T extends FieldsType = FieldsType>(
    entryId: string,
  ): Promise<IContentfulEntry<T>> {
    return this.storage
      .get(entryId)
      .then((value) => (!isNil(value) ? JSON.parse(value) : value));
  }

  public async resolveEntryCollection<T extends FieldsType>(
    collection: unknown,
  ): Promise<Array<IContentfulEntry<T>>> {
    const entries = this.asEntryArray<T>(collection);
    const resolvedEntries = await Promise.all(
      entries.map((entry) => this.resolveEntry(entry)),
    );

    return resolvedEntries.filter(
      (entry): entry is IContentfulEntry<T> => !isNil(entry),
    );
  }

  public checkRemoteUpdate(
    contentfulSpaceTagId: ContentfulSpaceTagId,
    contentfulContext: ContentfulContext,
    nextSyncToken?: string,
  ): Observable<ISyncCollection> {
    return this.sync(contentfulSpaceTagId, nextSyncToken, contentfulContext);
  }

  public syncEntries(
    syncCollection: ISyncCollection,
  ): Observable<ISyncCollection> {
    const store = (entries: Array<Entry<any>>): Observable<Array<any>> =>
      combineLatest(
        entries.map((entry) =>
          this.storage
            .set(getId(entry as IContentfulEntry<any>), JSON.stringify(entry))
            .then(() => this.contentfulStore.dispatch(updateEntriesCached())),
        ),
      );

    const sync = (): Observable<ISyncCollection> => {
      const { entries, deletedEntries } = syncCollection;
      if (isEmpty(entries) && isEmpty(deletedEntries)) {
        return of(syncCollection);
      }
      const updateEntries = store(entries);
      const deleteEntries = deletedEntries.map((entry) =>
        from(this.storage.remove(getId(entry))),
      );
      return combineLatest([updateEntries, ...deleteEntries]).pipe(
        mapTo(syncCollection),
      );
    };

    return sync();
  }

  public syncAssets(
    syncCollection: ISyncCollection,
  ): Observable<string | undefined> {
    const { assets, deletedAssets } = syncCollection;
    if (isEmpty(assets) && isEmpty(deletedAssets)) {
      return of(syncCollection.nextSyncToken);
    }
    const updateAssets = this.fetchAssets(assets).pipe(
      tap(() => this.contentfulStore.dispatch(assetsDownloaded())),
      switchMap((assetsToCache) => this.cacheAssets(assetsToCache)),
    );
    const deleteAssets = deletedAssets.map((asset) =>
      from(this.storage.remove(getId(asset))),
    );
    return combineLatest([updateAssets, ...deleteAssets]).pipe(
      mapTo(syncCollection.nextSyncToken),
    );
  }

  public clearCache(): Observable<void> {
    return from(this.storage.clear()).pipe(
      tap(() => this.contentfulStore.dispatch(clearNextSyncToken())),
    );
  }

  public updateNextToken(nextSyncToken: string) {
    this.contentfulStore.dispatch(nextSyncTokenUpdate({ nextSyncToken }));
  }

  public async presentNotificationIfHasChanges(
    syncCollection: ISyncCollection,
    override: boolean,
  ) {
    if (
      override ||
      (isEmpty(syncCollection.assets) && isEmpty(syncCollection.entries))
    ) {
      return Promise.resolve(undefined);
    }

    return new Promise((resolve, reject) => {
      this.alertService.presentDoubleActionAlert(
        'New content!',
        contentfulNewContentMessage,
        () => resolve(undefined),
        'Yes',
        reject,
        true,
      );
    });
  }

  public getRichTextHTML = async (richText: Document) => {
    const imgIds = this.getImageIds(richText);
    const imgSrcs = await Promise.all(
      imgIds.map((id) => this.storage.get(id) as Promise<string>),
    );
    const imgSrcMap = imgSrcs.reduce((result, value, index) => {
      Object.assign(result, { [imgIds[index]]: value });
      return result;
    }, {});
    return documentToHtmlString(richText, this.imageRendererOption(imgSrcMap));
  };

  private getImageIds(richText: Document): string[] {
    if (isNil(richText)) {
      return [];
    }
    const contents = richText.content.filter(
      (text) => text.nodeType === 'embedded-asset-block',
    );
    return contents.map((content) => content.data['target'].sys.id);
  }

  private imageRendererOption(
    srcMap: Record<string, string>,
  ): Partial<Options> {
    return {
      renderNode: {
        [BLOCKS.EMBEDDED_ASSET]: ({
          data: {
            target: { sys, fields },
          },
        }) =>
          !isNil(fields.file.details?.image)
            ? `<img src="${srcMap[sys.id]}" width="${
                fields.file.details?.image.width
              }"
                       alt="${fields.description}"/>`
            : '',
        [BLOCKS.EMBEDDED_ENTRY]: ({
          data: {
            target: { sys, fields },
          },
        }) => `<a rel="${sys.id}">${fields.commonName}</a>`,
        [BLOCKS.PARAGRAPH]: (node, next) =>
          `<p>${next(node.content).replace(/\n/g, '<br/>')}</p>`,
      },
    };
  }

  private fetchAssets(assets: Array<Asset>): Observable<Array<ICacheAsset>> {
    const assetsToFetch = assets
      .filter((asset) => !isNil(getAssetFile(asset)?.url))
      .map((asset) => this.constructAssetUrl(asset));
    return combineLatest(
      assetsToFetch.map((asset) =>
        this.http.get(asset.url, { responseType: 'blob' }).pipe(
          map((blob) => ({
            blob,
            type: asset.type,
            id: asset.id,
            url: asset.url,
          })),
          tap(() => this.contentfulStore.dispatch(updateAssetsDownloaded())),
        ),
      ),
    ) as Observable<Array<ICacheAsset>>;
  }

  private cacheAssets(assets: Array<ICacheAsset>) {
    const skippedAssets: Array<{ id: string; url: string }> = [];
    const withTimeout = <T>(
      promise: Promise<T>,
      timeoutMs: number,
      meta: { id: string; type: string; stage: string; url?: string },
    ) =>
      new Promise<T>((resolve, reject) => {
        const timer = setTimeout(() => {
          console.error('[Contentful] Asset timeout', {
            ...meta,
            timeoutMs,
          });
          reject(new Error('Asset operation timed out'));
        }, timeoutMs);
        promise
          .then((value) => {
            clearTimeout(timer);
            resolve(value);
          })
          .catch((err) => {
            clearTimeout(timer);
            reject(err);
          });
      });

    return combineLatest(
      assets.map(async (asset) => {
        try {
          const arrayBuffer = await withTimeout(
            asset.blob.arrayBuffer(),
            this.ASSET_READ_TIMEOUT_MS,
            {
              id: asset.id,
              type: asset.type,
              stage: 'read-arrayBuffer',
              url: asset.url,
            },
          );
          let result: string;
          if (asset.type.includes('image')) {
            const base64 = this.arrayBufferToBase64(arrayBuffer);
            result = `data:${asset.type};base64,${base64}`;
          } else if (typeof TextDecoder !== 'undefined') {
            result = new TextDecoder('utf-8').decode(
              new Uint8Array(arrayBuffer),
            );
          } else {
            console.warn('[Contentful] TextDecoder unavailable, using base64', {
              id: asset.id,
              type: asset.type,
              url: asset.url,
            });
            result = this.arrayBufferToBase64(arrayBuffer);
          }
          return withTimeout(
            this.storage
              .set(asset.id, result as string)
              .then(() => this.contentfulStore.dispatch(updateAssetsCached())),
            this.ASSET_STORE_TIMEOUT_MS,
            { id: asset.id, type: asset.type, stage: 'store', url: asset.url },
          );
        } catch (error) {
          console.warn('[Contentful] Skipping asset after read/store failure', {
            id: asset.id,
            type: asset.type,
            url: asset.url,
            error,
          });
          skippedAssets.push({ id: asset.id, url: asset.url });
          this.contentfulStore.dispatch(updateAssetsCached());
          return undefined;
        }
      }),
    ).pipe(
      tap(() => {
        if (skippedAssets.length > 0) {
          console.warn('[Contentful] Skipped assets', {
            assets: skippedAssets,
          });
        }
      }),
    );
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode(...chunk);
    }
    return btoa(binary);
  }

  private constructAssetUrl(asset: Asset): {
    id: string;
    url: string;
    type: string;
  } {
    return {
      id: getId(asset),
      type: getAssetFile(asset)?.contentType,
      url: `https:${getAssetFile(asset)?.url}?w=${window.innerWidth}`,
    };
  }

  private hasTag = (item: Asset | Entry, tagId: string): boolean => {
    const tags = item.metadata.tags;
    if (!Array.isArray(tags)) return false;
    return tags.some((t) => t.sys.id === tagId);
  };

  private filterSyncByTag = (
    contentfulAppContext: string,
    syncCollection: ISyncCollection,
  ): ISyncCollection => ({
    ...syncCollection,
    entries: syncCollection.entries.filter((e) =>
      this.hasTag(e, contentfulAppContext),
    ),
    assets: syncCollection.assets.filter((a) =>
      this.hasTag(a, contentfulAppContext),
    ),
  });

  private fetchResolvedEntriesForInitialSync = (
    contentfulAppContext: ContentfulSpaceTagId,
    syncCollection: ISyncCollection,
    contentfulContext: ContentfulContext,
    nextSyncToken?: string,
  ) =>
    !nextSyncToken
      ? from(
          this.getContentfulClient(contentfulContext).getEntries({
            limit: 1000,
            'metadata.tags.sys.id[in]': [contentfulAppContext],
          }),
        ).pipe(
          map((entries) => ({
            ...syncCollection,
            entries: entries.items,
          })),
        )
      : of(syncCollection);

  private updateEntriesAssetsToDownload = (syncCollection: ISyncCollection) => {
    const entriesToCache = syncCollection.entries.length;
    const assetsToCache = syncCollection.assets.length;
    this.contentfulStore.dispatch(updateEntriesToCache({ entriesToCache }));
    this.contentfulStore.dispatch(updateAssetsToCache({ assetsToCache }));
  };

  private sync = (
    contentfulSpaceTagId: ContentfulSpaceTagId,
    nextSyncToken: string | undefined,
    contentfulContext: ContentfulContext,
  ): Observable<ISyncCollection> => {
    const syncCollection$ = this.getContentfulClient(
      contentfulContext,
    ).withoutLinkResolution.sync({
      ...(nextSyncToken ? { nextSyncToken } : { initial: true }),
    }) as Promise<ISyncCollection>;
    return from(syncCollection$).pipe(
      map((syncCollection) =>
        this.filterSyncByTag(contentfulSpaceTagId, syncCollection),
      ),
      switchMap((syncCollection) =>
        this.fetchResolvedEntriesForInitialSync(
          contentfulSpaceTagId,
          syncCollection,
          contentfulContext,
          nextSyncToken,
        ),
      ),
      tap(this.updateEntriesAssetsToDownload),
    );
  };

  private async resolveEntry<T extends FieldsType>(
    entry: IContentfulEntry<T>,
  ): Promise<IContentfulEntry<T> | undefined> {
    if (this.hasFields(entry)) {
      return entry;
    }

    const id = this.getEntryId(entry);
    return id ? this.getEntryById<T>(id) : undefined;
  }

  private asEntryArray<T extends FieldsType>(
    collection: unknown,
  ): Array<IContentfulEntry<T>> {
    const items = this.readCollectionItems(collection);

    return items.filter((item): item is IContentfulEntry<T> =>
      this.isContentfulEntry<T>(item),
    );
  }

  private readCollectionItems(collection: unknown): unknown[] {
    if (Array.isArray(collection)) {
      return collection;
    }

    if (!this.isRecord(collection)) {
      return [];
    }

    const localizedItems = collection['en-US'];
    if (Array.isArray(localizedItems)) {
      return localizedItems;
    }

    const values = Object.values(collection);
    return values.every(Array.isArray) ? values.flat() : values;
  }

  private hasFields<T extends FieldsType>(entry: IContentfulEntry<T>): boolean {
    return this.isRecord(entry.fields);
  }

  private isContentfulEntry<T extends FieldsType>(
    value: unknown,
  ): value is IContentfulEntry<T> {
    return this.isRecord(value) && this.isRecord(value['sys']);
  }

  private getEntryId<T extends FieldsType>(
    entry: IContentfulEntry<T>,
  ): string | undefined {
    const id = entry.sys.id;
    return typeof id === 'string' ? id : undefined;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && !isNil(value);
  }
}
